import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { CRM_PRIVACY_EVENT, clearCrmActivity, crmAnalyticsAllowed, crmRoute, flushCrmActivity, trackCrmEvent } from '@/services/crmActivity';

/** Observational analytics only; all business states come from the server's own records. */
export function CrmActivity() {
  const { pathname } = useLocation();
  const { userId, userRole } = useAuth();
  const { wishlist } = useWishlist();
  const [allowed, setAllowed] = useState(crmAnalyticsAllowed);
  const previousWishlist = useRef<string[]>(wishlist.map(item => item.id));
  const enabled = allowed && userRole !== 'admin' && !pathname.startsWith('/admin');
  useEffect(() => {
    const change = () => {
      const value = crmAnalyticsAllowed(); setAllowed(value);
      if (!value) clearCrmActivity();
    };
    window.addEventListener(CRM_PRIVACY_EVENT, change);
    window.addEventListener('storage', change);
    return () => { window.removeEventListener(CRM_PRIVACY_EVENT, change); window.removeEventListener('storage', change); };
  }, []);
  useEffect(() => {
    previousWishlist.current = wishlist.map(item => item.id);
    // Auth changes cause the service to rotate the visitor ID before accepting another event.
    if (enabled) trackCrmEvent('session.started');
    // The identity boundary, rather than wishlist changes, defines a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, enabled]);
  useEffect(() => {
    const route = crmRoute(pathname);
    if (!enabled || !route) return;
    trackCrmEvent('page.viewed', { route });
    if (route === '/checkout') trackCrmEvent('checkout.viewed', { route });
  }, [pathname, enabled, userId]);
  useEffect(() => {
    const current = wishlist.map(item => item.id);
    if (enabled) {
      current.filter(id => !previousWishlist.current.includes(id)).forEach(productId => trackCrmEvent('wishlist.item_added', { productId }));
      previousWishlist.current.filter(id => !current.includes(id)).forEach(productId => trackCrmEvent('wishlist.item_removed', { productId }));
    }
    previousWishlist.current = current;
  }, [wishlist, enabled]);
  useEffect(() => {
    if (!enabled) return;
    const flush = () => { void flushCrmActivity(); };
    const timer = window.setInterval(flush, 2000);
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', flush);
    return () => { window.clearInterval(timer); window.removeEventListener('pagehide', flush); document.removeEventListener('visibilitychange', flush); };
  }, [enabled]);
  return null;
}
